import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck,
  Search,
  Plus,
  Trash2,
  Crown,
  History,
  CheckCircle2,
  XCircle,
  Clock,
  Server,
  Users,
  Terminal,
  Activity,
  ArrowUpRight,
  Database,
  Cpu,
  AlertTriangle,
  RefreshCw,
  Tag,
  Calendar,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export function Admin() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [diagResults, setDiagResults] = useState<any[] | null>(null);

  // Promotion States
  const [pCode, setPCode] = useState("");
  const [pNote, setPNote] = useState("");
  const [pPct, setPPct] = useState(10);
  const [pMax, setPMax] = useState(50);
  const [pFrom, setPFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [pTo, setPTo] = useState(() => {
    const x = new Date();
    x.setMonth(x.getMonth() + 1);
    return x.toISOString().slice(0, 10);
  });
  const [pMonthly, setPMonthly] = useState(true);
  const [pYearly, setPYearly] = useState(true);
  const [pLife, setPLife] = useState(false);

  // Queries
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => api.adminStats(),
    staleTime: 0,
    refetchOnMount: true
  });

  const { data: premiumList, isLoading: loadingPrem } = useQuery({
    queryKey: ["admin", "premium"],
    queryFn: () => api.adminPremium(),
    refetchInterval: 10000,
    staleTime: 0,
  });

  const { data: pendingReqs } = useQuery({
    queryKey: ["admin", "requests", "pending"],
    queryFn: () => api.adminPremiumRequests("pending"),
  });

  const { data: allBotGuilds, isLoading: loadingAllGuilds } = useQuery({
    queryKey: ["admin", "guilds"],
    queryFn: () => api.adminGuilds(),
  });

  const { data: adminLogs } = useQuery({
    queryKey: ["admin", "logs"],
    queryFn: () => api.adminLogs(),
    refetchInterval: 10000
  });

  const { data: promoData } = useQuery({
    queryKey: ["admin", "promotions"],
    queryFn: () => api.adminPromotions(),
  });

  // Mutations
  const grantMut = useMutation({
    mutationFn: (body: { target_id: string; plan: string; days: number; is_user: boolean }) => api.grantPremium(body.target_id, body.plan, body.days, body.is_user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "premium"] });
      toast.success("Premium granted successfully");
    },
  });

  const revokeMut = useMutation({
    mutationFn: (body: { target_id: string; is_user: boolean }) => api.revokePremium(body.target_id, body.is_user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "premium"] });
      toast.success("Premium revoked");
    },
  });

  const approveMut = useMutation({
    mutationFn: (id: string) => api.adminApproveRequest(id),
    onSuccess: () => {
       qc.invalidateQueries({ queryKey: ["admin", "requests"] });
       qc.invalidateQueries({ queryKey: ["admin", "premium"] });
       toast.success("Request approved");
    }
  });

  const rejectMut = useMutation({
    mutationFn: ({id, reason}: {id: string, reason: string}) => api.adminRejectRequest(id, reason),
    onSuccess: () => {
       qc.invalidateQueries({ queryKey: ["admin", "requests"] });
       toast.success("Request rejected");
    }
  });

  const diagMut = useMutation({
    mutationFn: () => api.adminDiagnostics(),
    onSuccess: (data) => {
      setDiagResults(data.results);
      toast.success("Diagnostics completed");
    },
    onError: () => toast.error("Diagnostics failed")
  });

  const refreshGuildsMut = useMutation({
    mutationFn: () => api.adminRefreshGuilds(),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      toast.success(`Sync complete: ${data.count} guilds found`);
    },
    onError: (err: any) => {
      console.error("[Admin] Sync failed! Full error object:", err);
      const msg = err.body?.error || err.message || "Unknown error";
      toast.error(`Guild sync failed: ${msg}`);
    }
  });

  // Promotion Mutations
  const createPromoMut = useMutation({
    mutationFn: (body: any) => api.adminPromotionCreate(body),
    onSuccess: () => {
      toast.success("Promotion created");
      setPCode("");
      setPNote("");
      qc.invalidateQueries({ queryKey: ["admin", "promotions"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const patchPromoMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => api.adminPromotionPatch(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "promotions"] }),
    onError: (e: any) => toast.error(e.message),
  });

  const deletePromoMut = useMutation({
    mutationFn: (id: string) => api.adminPromotionDelete(id),
    onSuccess: () => {
      toast.success("Promotion deleted");
      qc.invalidateQueries({ queryKey: ["admin", "promotions"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  function submitPromotion() {
    const plan_ids: string[] = [];
    if (pMonthly) plan_ids.push("monthly");
    if (pYearly) plan_ids.push("yearly");
    if (pLife) plan_ids.push("lifetime");
    if (plan_ids.length === 0) {
      toast.error("Select at least one plan.");
      return;
    }
    const body = {
      code: pCode.trim().toUpperCase(),
      note: pNote.trim(),
      percent_off: pPct,
      plan_ids,
      valid_from: pFrom,
      valid_until: pTo,
      max_uses: pMax,
      enabled: true,
    };
    createPromoMut.mutate(body);
  }

  const [premSearch, setPremSearch] = useState("");

  // Guild grants (the normal, correctly-scoped kind — see webhooks.js) were previously
  // missing here entirely, so the "Status" column below (which only matches type ===
  // "guild") never found anything and showed every server as STANDARD regardless of
  // whether it actually had premium. Both types are now included.
  const premiumArray = [
    ...(premiumList?.guilds ? Object.entries(premiumList.guilds).map(([id, data]: [string, any]) => ({
      target_id: id,
      type: "guild",
      ...data
    })) : []),
    ...(premiumList?.users ? Object.entries(premiumList.users).map(([id, data]: [string, any]) => ({
      target_id: id,
      type: "user",
      ...data
    })) : []),
  ];

  const filteredPremiumArray = premiumArray.filter(g => 
    g.target_id.toLowerCase().includes(premSearch.toLowerCase())
  );

  // Guild grants (the correctly-scoped kind — see webhooks.js) were previously left out
  // of this count entirely, which undercounted real active subscriptions.
  const totalPremiumActive = (stats?.total_premium_guilds || 0) + (stats?.total_premium_users || 0);

  // Robust value for Active Servers card
  const activeServersCount = (stats?.bot_guild_count && stats.bot_guild_count > 0) 
    ? stats.bot_guild_count 
    : (stats?.total_guilds_configured || 0);

  const NAV_CLS =
    "relative shrink-0 lg:w-full justify-start gap-2.5 rounded-lg pl-3 pr-2.5 py-2.5 text-[13px] font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground hover:bg-accent/40 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none data-[state=active]:before:absolute data-[state=active]:before:left-0 data-[state=active]:before:top-1/2 data-[state=active]:before:-translate-y-1/2 data-[state=active]:before:h-5 data-[state=active]:before:w-[3px] data-[state=active]:before:rounded-full data-[state=active]:before:bg-primary";

  return (
    <div className="max-w-[1400px] mx-auto pb-20 px-4 pt-5">
      <Toaster position="top-right" theme="dark" richColors />

      <Tabs
        orientation="vertical"
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col lg:flex-row gap-5 items-start"
      >
        {/* ===== Console nav — unified panel ===== */}
        <aside className="w-full lg:w-[240px] shrink-0 lg:sticky lg:top-6">
          <div className="lg:rounded-2xl lg:border lg:border-border/50 lg:bg-card/50 lg:overflow-hidden">
            {/* Brand header */}
            <div className="hidden lg:flex items-center gap-2.5 p-4 border-b border-border/40">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 ring-1 ring-primary/20 flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">Control Center</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] font-mono text-emerald-400">API ONLINE</span>
                </div>
              </div>
            </div>

            <TabsList className="flex flex-row lg:flex-col h-auto w-full bg-transparent p-2 gap-0.5 overflow-x-auto lg:overflow-visible no-scrollbar justify-start">
              <span className="hidden lg:block px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">General</span>
              <TabsTrigger value="overview" className={NAV_CLS}>
                <Activity className="h-4 w-4 shrink-0" /> Overview
              </TabsTrigger>
              <TabsTrigger value="servers" className={NAV_CLS}>
                <Server className="h-4 w-4 shrink-0" /> Servers
              </TabsTrigger>
              <TabsTrigger value="infrastructure" className={NAV_CLS}>
                <Terminal className="h-4 w-4 shrink-0" /> Infrastructure
              </TabsTrigger>
              <TabsTrigger value="logs" className={NAV_CLS}>
                <History className="h-4 w-4 shrink-0" /> Logs
              </TabsTrigger>

              <span className="hidden lg:block px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">Billing</span>
              <TabsTrigger value="premium" className={NAV_CLS}>
                <Crown className="h-4 w-4 shrink-0" /> Premium
              </TabsTrigger>
              <TabsTrigger value="requests" className={NAV_CLS}>
                <History className="h-4 w-4 shrink-0" /> Requests
                {pendingReqs?.requests && pendingReqs.requests.length > 0 && (
                  <span className="ml-auto flex h-5 min-w-5 px-1 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground font-bold">
                    {pendingReqs.requests.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="promotions" className={NAV_CLS}>
                <Tag className="h-4 w-4 shrink-0" /> Promotions
              </TabsTrigger>
            </TabsList>

            {/* Sync button */}
            <div className="hidden lg:block p-2 pt-0">
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2"
                onClick={() => refreshGuildsMut.mutate()}
                disabled={refreshGuildsMut.isPending}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${refreshGuildsMut.isPending ? "animate-spin" : ""}`} />
                Sync Guilds
              </Button>
            </div>
          </div>
        </aside>

        {/* ===== Main ===== */}
        <main className="flex-1 min-w-0 w-full space-y-6">
          {/* Stat strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatsCard label="Active Servers" value={activeServersCount} icon={Server} trend="Real-time" color="blue" />
            <StatsCard label="Economy Players" value={stats?.total_economy_users || 0} icon={Users} trend="Total" color="purple" />
            <StatsCard label="Premium Active" value={totalPremiumActive} icon={Crown} trend="Subs" color="amber" />
            <StatsCard label="System Health" value={formatUptime(stats?.uptime || 0)} icon={Cpu} trend="Online" color="emerald" />
          </div>

        <TabsContent value="overview" className="space-y-5 mt-0">
          <div className="grid lg:grid-cols-3 gap-5">
            {/* Recent activity — real admin logs */}
            <Card className="lg:col-span-2 border-border/50 bg-card/50 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="h-4.5 w-4.5 text-primary" />
                  Recent activity
                </h3>
                <Button variant="ghost" size="sm" className="text-primary text-xs h-8" onClick={() => setActiveTab("logs")}>
                  View all
                </Button>
              </div>
              <div className="divide-y divide-border/40">
                {adminLogs?.logs?.slice(0, 6).map((log: any) => {
                  const isGrant = log.action?.includes("grant");
                  const isRevoke = log.action?.includes("revoke");
                  return (
                    <div key={log.id} className="flex items-center gap-3 px-5 py-3.5">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                        isGrant ? "bg-emerald-500/10 text-emerald-400" :
                        isRevoke ? "bg-destructive/10 text-destructive" :
                        "bg-primary/10 text-primary"
                      }`}>
                        {isGrant ? <Crown className="h-4 w-4" /> : isRevoke ? <Trash2 className="h-4 w-4" /> : <Activity className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium capitalize truncate">
                          {String(log.action || "action").replace(/_/g, " ")}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          Target: <span className="font-mono">{log.details?.target_id || log.details?.guild_id || "system"}</span>
                        </p>
                      </div>
                      <span className="text-[11px] text-muted-foreground/60 shrink-0">
                        {log.timestamp ? new Date(log.timestamp * 1000).toLocaleDateString() : ""}
                      </span>
                    </div>
                  );
                })}
                {(!adminLogs?.logs || adminLogs.logs.length === 0) && (
                  <div className="px-5 py-16 text-center">
                    <div className="h-10 w-10 rounded-full bg-muted/40 flex items-center justify-center mx-auto mb-3">
                      <Clock className="h-5 w-5 text-muted-foreground/40" />
                    </div>
                    <p className="text-sm text-muted-foreground">No recent activity yet.</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Global presence + health */}
            <Card className="border-border/50 bg-card/50 overflow-hidden">
              <div className="px-5 py-4 border-b border-border/40">
                <h3 className="font-semibold flex items-center gap-2">
                  <Database className="h-4.5 w-4.5 text-primary" />
                  Bot presence
                </h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="rounded-2xl bg-primary/10 ring-1 ring-primary/20 p-4 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Active servers (verified)</p>
                  <p className="text-4xl font-semibold tracking-tight text-primary tabular-nums">
                    {stats?.bot_guild_count ?? "—"}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="rounded-xl border border-border/40 bg-background/40 p-3">
                    <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Configured</p>
                    <p className="text-lg font-semibold tabular-nums">{stats?.total_guilds_configured || 0}</p>
                  </div>
                  <div className="rounded-xl border border-border/40 bg-background/40 p-3">
                    <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Premium</p>
                    <p className="text-lg font-semibold tabular-nums text-amber-400">{totalPremiumActive}</p>
                  </div>
                </div>

                <div className="pt-3 border-t border-border/40">
                  <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">System health</h4>
                  <div className="space-y-1">
                    <HealthItem label="API Response" status="Optimal" value="42ms" />
                    <HealthItem label="Shard Connections" status="Optimal" value="Online" />
                    <HealthItem label="Database Latency" status="Optimal" value="12ms" />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="premium">
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="font-bold text-xl">Grant Premium Access</h3>
                  <p className="text-sm text-muted-foreground">Manually grant premium to any Discord user.</p>
                </div>
              </div>
              <GrantForm onGrant={(body: any) => grantMut.mutate(body)} loading={grantMut.isPending} />
            </Card>

            <Card className="overflow-hidden border-primary/10">
              <div className="p-6 border-b bg-muted/30 flex items-center justify-between gap-4">
                 <h3 className="font-bold shrink-0">Active Premium Subscriptions</h3>
                 <div className="relative max-w-xs w-full">
                   <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                   <Input 
                     placeholder="Search ID..." 
                     className="pl-9 h-9" 
                     value={premSearch}
                     onChange={e => setPremSearch(e.target.value)}
                   />
                 </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase text-muted-foreground bg-muted/20">
                    <tr>
                      <th className="px-6 py-4 font-bold">Target ID</th>
                      <th className="px-6 py-4 font-bold">Type</th>
                      <th className="px-6 py-4 font-bold">Plan</th>
                      <th className="px-6 py-4 font-bold">Expires</th>
                      <th className="px-6 py-4 font-bold">Order Details</th>
                      <th className="px-6 py-4 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    <AnimatePresence>
                      {filteredPremiumArray.map((g: any) => (
                        <motion.tr 
                          key={g.target_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="hover:bg-muted/10 transition-colors group"
                        >
                          <td className="px-6 py-4 font-mono font-medium text-primary">{g.target_id}</td>
                          <td className="px-6 py-4">
                            {g.type === "guild" ? (
                              <Badge variant="outline" className="capitalize">Guild</Badge>
                            ) : (
                              <Badge variant="outline" className="capitalize text-amber-500 border-amber-500/30" title="Personal grant — only counts toward a server if this person is that server's actual owner.">
                                User
                              </Badge>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <Badge variant="premium" className="capitalize">{g.plan}</Badge>
                          </td>
                          <td className="px-6 py-4 text-muted-foreground">
                            {Number(g.expires_at) === -1 || Number(g.expires_at) === 0 ? "Forever" : new Date(Number(g.expires_at) * 1000).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-xs text-muted-foreground flex flex-col gap-1">
                            <span><strong className="text-foreground/80">Bought:</strong> {g.granted_at ? new Date(Number(g.granted_at) * 1000).toLocaleDateString() : "N/A"}</span>
                            <span><strong className="text-foreground/80">ID:</strong> {g.transaction_id || "Manual"}</span>
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => revokeMut.mutate({ target_id: g.target_id, is_user: g.type === "user" })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                    {filteredPremiumArray.length === 0 && !loadingPrem && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground italic">
                          No premium subscriptions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="requests">
          <Card className="overflow-hidden">
            <div className="p-6 border-b bg-muted/30">
               <h3 className="font-bold text-xl">Pending Payment Requests</h3>
               <p className="text-sm text-muted-foreground mt-1">Review manual proof-of-payment submissions from users.</p>
            </div>
            <div className="p-6 space-y-4">
               <AnimatePresence mode="popLayout">
                 {pendingReqs?.requests?.map((req: any) => (
                   <motion.div 
                     key={req.id}
                     layout
                     initial={{ opacity: 0, scale: 0.95 }}
                     animate={{ opacity: 1, scale: 1 }}
                     exit={{ opacity: 0, scale: 0.95 }}
                     className="p-5 rounded-xl border bg-card/50 flex flex-col md:flex-row md:items-center justify-between gap-6"
                   >
                     <div className="space-y-2">
                        <div className="flex items-center gap-2">
                           <Badge variant="outline" className="font-mono">{req.guild_id}</Badge>
                           <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                           <span className="text-sm font-semibold">{req.plan} Plan</span>
                           <Badge variant="secondary" className="text-[10px] uppercase font-bold">{req.payment_method}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">Requested by <span className="text-foreground font-mono">{req.user_id}</span></p>
                        <div className="text-[10px] text-muted-foreground bg-muted/30 px-2 py-1 rounded inline-flex items-center gap-2">
                           Ref: <span className="text-foreground font-mono select-all">{req.payment_reference}</span>
                        </div>
                        {req.message && (
                          <div className="mt-2 p-3 rounded-lg bg-background/50 border text-xs text-muted-foreground italic">
                            "{req.message}"
                          </div>
                        )}
                     </div>
                     <div className="flex items-center gap-2 shrink-0">
                        <Button 
                          variant="outline" 
                          className="border-destructive/30 text-destructive hover:bg-destructive/10"
                          onClick={() => rejectMut.mutate({ id: req.id, reason: "Manual rejection" })}
                        >
                           <XCircle className="h-4 w-4 mr-2" /> Reject
                        </Button>
                        <Button 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => approveMut.mutate(req.id)}
                        >
                           <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
                        </Button>
                     </div>
                   </motion.div>
                 ))}
               </AnimatePresence>
               {(!pendingReqs?.requests || pendingReqs.requests.length === 0) && (
                 <div className="py-20 text-center space-y-3">
                    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto">
                       <CheckCircle2 className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                    <p className="text-muted-foreground">Everything caught up! No pending requests.</p>
                 </div>
               )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="servers" className="animate-fade-in">
           <Card className="overflow-hidden border-primary/10">
              <div className="p-6 border-b bg-muted/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                 <div>
                   <h3 className="font-bold text-xl">All Bot Servers</h3>
                   <p className="text-sm text-muted-foreground">Detailed list of all guilds where Areon is currently active.</p>
                 </div>
                 <div className="relative max-w-xs w-full">
                   <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                   <Input placeholder="Search name or ID..." className="pl-9 h-9" />
                 </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase text-muted-foreground bg-muted/20 font-bold">
                    <tr>
                      <th className="px-6 py-4">Guild</th>
                      <th className="px-6 py-4">ID</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {loadingAllGuilds ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground animate-pulse">
                          Fetching all server data from Discord...
                        </td>
                      </tr>
                    ) : (
                      allBotGuilds?.guilds?.map((g) => (
                        <tr key={g.id} className="hover:bg-muted/10 transition-colors group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {g.icon ? (
                                <img
                                  src={`https://cdn.discordapp.com/icons/${g.id}/${g.icon}.png?size=64`}
                                  alt=""
                                  className="h-8 w-8 rounded-lg shrink-0 border border-border/50"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground border border-border/50">
                                  {g.name.slice(0, 2).toUpperCase()}
                                </div>
                              )}
                              <span className="font-semibold text-foreground truncate max-w-[200px]">{g.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{g.id}</td>
                          <td className="px-6 py-4">
                            {g.is_premium ? (
                              <Badge variant="premium" className="text-[10px]">PREMIUM</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-muted-foreground/60 border-muted-foreground/20">STANDARD</Badge>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <Button asChild size="sm" variant="outline" className="gap-1.5">
                              <Link to={`/server/${g.id}`}>
                                <ArrowUpRight className="h-3.5 w-3.5" />
                                Manage
                              </Link>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                    {(!allBotGuilds?.guilds || allBotGuilds.guilds.length === 0) && !loadingAllGuilds && (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground italic">
                          No servers found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
           </Card>
        </TabsContent>
        <TabsContent value="infrastructure" className="animate-fade-in">
           <Card className="p-6 sm:p-8 space-y-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="space-y-1">
                  <h3 className="text-2xl font-black tracking-tight">System Infrastructure</h3>
                  <p className="text-sm text-muted-foreground">Monitor bot nodes, database health, and API connectivity.</p>
                </div>
                <Button 
                   variant="outline" 
                   onClick={() => diagMut.mutate()} 
                   disabled={diagMut.isPending}
                   className="gap-2 border-primary/20 hover:bg-primary/5"
                >
                   {diagMut.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
                   Run Full Diagnostics
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                 {diagResults ? (
                    diagResults.map((res: any, i: number) => (
                       <div key={i} className="p-5 rounded-2xl border bg-card/30 space-y-3">
                          <div className="flex items-center justify-between">
                             <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{res.name}</span>
                             {res.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-destructive" />}
                          </div>
                          <p className="text-sm font-semibold">{res.message}</p>
                          <div className={`h-1.5 w-full rounded-full ${res.ok ? 'bg-emerald-500/20' : 'bg-destructive/20'}`}>
                             <div className={`h-full rounded-full ${res.ok ? 'bg-emerald-500' : 'bg-destructive'}`} style={{width: res.ok ? '100%' : '30%'}} />
                          </div>
                       </div>
                    ))
                 ) : (
                    <div className="col-span-full py-12 text-center border-dashed border-2 rounded-2xl bg-muted/20">
                       <p className="text-muted-foreground">Run diagnostics to see infrastructure health.</p>
                    </div>
                 )}
              </div>
           </Card>
        </TabsContent>

        <TabsContent value="promotions" className="animate-fade-in space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <h3 className="text-xl font-bold">Promotions / Discount Codes</h3>
              <p className="text-sm text-muted-foreground">Create coupon codes for users to redeem during checkout.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code (e.g. AREON50)</Label>
                <Input placeholder="CODE" value={pCode} onChange={e => setPCode(e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input type="number" value={pPct} onChange={e => setPPct(Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>Max Uses (0 = ∞)</Label>
                <Input type="number" value={pMax} onChange={e => setPMax(Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>Valid From</Label>
                <Input type="date" value={pFrom} onChange={e => setPFrom(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Valid Until</Label>
                <Input type="date" value={pTo} onChange={e => setPTo(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Internal Note</Label>
                <Input placeholder="Note..." value={pNote} onChange={e => setPNote(e.target.value)} />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Applicable Plans</Label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={pMonthly} onChange={e => setPMonthly(e.target.checked)} className="rounded border-border" />
                  <span className="text-sm">Monthly</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={pYearly} onChange={e => setPYearly(e.target.checked)} className="rounded border-border" />
                  <span className="text-sm">Yearly</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={pLife} onChange={e => setPLife(e.target.checked)} className="rounded border-border" />
                  <span className="text-sm">Lifetime</span>
                </label>
              </div>
            </div>

            <Button onClick={submitPromotion} disabled={!pCode || createPromoMut.isPending} className="gap-2">
              <Tag className="h-4 w-4" /> Create Coupon Code
            </Button>
          </Card>

          <Card className="overflow-hidden border-primary/10">
            <div className="p-6 border-b bg-muted/30">
              <h3 className="font-bold">Active Promotions</h3>
            </div>
            <div className="divide-y divide-border/40">
              {promoData?.promotions?.map((p: any) => (
                <div key={p.id} className="p-4 flex items-center justify-between gap-4 bg-card/30">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-bold text-primary">{p.code}</code>
                      <Badge variant="premium">{p.percent_off}% OFF</Badge>
                      <span className="text-xs text-muted-foreground">
                        {p.uses} / {p.max_uses === 0 ? "∞" : p.max_uses} uses
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
                      {p.valid_from} → {p.valid_until} · {p.plan_ids?.join(", ")}
                    </p>
                    {p.note && <p className="text-xs italic text-muted-foreground/60">{p.note}</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-muted-foreground">Enabled</span>
                      <Switch 
                        checked={p.enabled} 
                        onCheckedChange={(v: boolean) => patchPromoMut.mutate({ id: p.id, body: { enabled: v } })}
                      />
                    </div>
                    <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => deletePromoMut.mutate(p.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {(!promoData?.promotions || promoData.promotions.length === 0) && (
                <div className="p-12 text-center text-muted-foreground italic">No promotions found.</div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="animate-fade-in">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold">Admin Activity Logs</h3>
                <p className="text-sm text-muted-foreground">History of all administrative actions performed on the platform.</p>
              </div>
            </div>
            <div className="space-y-3">
              {adminLogs?.logs?.map((log: any) => (
                <div key={log.id} className="flex items-center gap-4 p-4 rounded-xl border bg-card/30">
                  <div className={`p-2 rounded-lg ${
                    log.action.includes("grant") ? "bg-emerald-500/10 text-emerald-500" :
                    log.action.includes("revoke") ? "bg-destructive/10 text-destructive" :
                    "bg-primary/10 text-primary"
                  }`}>
                    {log.action.includes("grant") ? <Crown className="h-4 w-4" /> :
                     log.action.includes("revoke") ? <Trash2 className="h-4 w-4" /> :
                     <Activity className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">
                      <span className="text-primary uppercase text-[10px] mr-2">[{log.action}]</span>
                      Admin <span className="font-mono text-xs">{log.user_id}</span> performed action on {log.details.target_id || log.details.guild_id || "System"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {JSON.stringify(log.details)}
                    </p>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {new Date(log.timestamp * 1000).toLocaleString()}
                  </span>
                </div>
              ))}
              {(!adminLogs?.logs || adminLogs.logs.length === 0) && (
                <div className="py-10 text-center text-muted-foreground italic">No logs found yet.</div>
              )}
            </div>
          </Card>
        </TabsContent>
        </main>
      </Tabs>
    </div>
  );
}

function StatsCard({ label, value, icon: Icon, color }: any) {
  const colorMap: any = {
    blue: "text-blue-400 bg-blue-400/10",
    purple: "text-purple-400 bg-purple-400/10",
    amber: "text-amber-400 bg-amber-400/10",
    emerald: "text-emerald-400 bg-emerald-400/10",
  };

  return (
    <Card className="p-4 border-border/50 bg-card/50 hover:bg-card transition-colors">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
      </div>
      <p className="text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : (value || "0")}
      </p>
    </Card>
  );
}

function HealthItem({ label, status, value }: any) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2.5">
        <div className={`h-1.5 w-1.5 rounded-full ${status === 'Optimal' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
        <span className="text-[13px] text-foreground/80">{label}</span>
      </div>
      <span className="text-xs font-mono text-muted-foreground">{value}</span>
    </div>
  );
}

function GrantForm({ onGrant, loading }: any) {
  const [gid, setGid] = useState("");
  const [days, setDays] = useState("30");
  const [plan, setPlan] = useState("monthly");

  return (
    <div className="flex flex-col md:flex-row gap-3">
      <div className="flex-1">
        <Input 
          placeholder="Discord User ID (Snowflake)" 
          value={gid} 
          onChange={e => setGid(e.target.value)}
          className="h-11 bg-background/50"
        />
      </div>
      <div className="w-full md:w-40">
        <select 
          value={plan} 
          onChange={e => {
            setPlan(e.target.value);
            if(e.target.value === "monthly") setDays("30");
            if(e.target.value === "yearly") setDays("365");
            if(e.target.value === "lifetime") setDays("0");
          }}
          className="h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="monthly">Monthly (30d)</option>
          <option value="yearly">Yearly (365d)</option>
          <option value="lifetime">Lifetime (Forever)</option>
        </select>
      </div>
      <div className="w-full md:w-32">
        <Input 
          type="number" 
          placeholder="Days" 
          value={days} 
          onChange={e => setDays(e.target.value)}
          className="h-11 bg-background/50"
          disabled={plan === "lifetime"}
        />
      </div>
      <Button 
        className="h-11 px-8 gap-2 bg-primary hover:bg-primary/90" 
        onClick={() => {
          if (!gid) return toast.error("User ID is required");
          onGrant({ target_id: gid, plan, days: parseInt(days) || 0, is_user: true });
          setGid("");
        }}
        disabled={loading}
      >
        <Plus className="h-4 w-4" /> Grant Premium
      </Button>
    </div>
  );
}

function formatUptime(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}
